/**
 * ListingsPager — the CRAWLABLE half of the listings hub's pagination.
 *
 * THE DEFECT THIS CLOSES. `/listings/rent` and `/listings/buy` (and the district
 * and dwelling-type facets, which render the same sheet) page ONLY through the
 * infinite-scroll handler in `ListingsSheet`/`MobileSheet`: a scroll listener
 * calls `onLoadMore`, TanStack fetches `page + 1`, and the cards append. The
 * loader has read `?page=` since the hub existed (`hub.loader.ts`,
 * `pageFromSearchParam`) and the backend answers page N with page N's own
 * self-canonical and "— Page N" title — but NO `<a href="…?page=2">` existed
 * anywhere in the server HTML. So a crawler entering the hub could reach
 * exactly the first `SEARCH_PAGE_SIZE` cards and not one more: LET-83 gave the
 * cards their anchors, and this is the same class one level up — a
 * user-reachable navigation target the served HTML does not express as a link.
 *
 * The shape is the one `PaginatedDirectory` (`@letsgethome/ui`) already gives
 * the blog/partners/estates directories: real anchors, page 1 canonicalised to
 * the bare route (no `?page=1` duplicate), `rel="prev"`/`rel="next"` for the
 * crawler. That door is not reused verbatim because it also owns the item
 * grid, and the hub's grid is the two-pane map/list sheet with its own
 * selection ring and scroll-into-view; only the PAGER half is wanted here.
 *
 * WHAT IT IS DERIVED FROM, deliberately: the SSR document's OWN page and
 * `hasMore` (the loader's `initialMeta`), never the infinite query's live
 * `hasNextPage`. The anchors are the crawl contract for THIS document, so they
 * must be the same in the served HTML and after hydration — the link-parity
 * gate (`web/e2e/public/link-parity.spec.ts`) asserts exactly that, and a pager
 * that re-derived itself from an accumulating scroll would drift under it.
 * Infinite scroll stays the enhancement on top.
 */
import { Link, useSearchParams } from '@/shared/i18n/routing';
import { useTranslations } from '@/shared/i18n/useTranslations';
import { assertNever } from '@letsgethome/lib/utils/enums';
import { applyListingsPage, withQuery, type Route } from '@letsgethome/routes';
import type { PageStanding } from '@letsgethome/types/paginated';
import { buttonVariants } from '@letsgethome/ui/button';
import { cn } from '@letsgethome/ui/lib/utils';
import { usePagerChrome } from '@letsgethome/ui/pager-chrome';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useCallback } from 'react';

/**
 * The page-N href builder for a listings surface, from that surface's BASE
 * route and the CURRENT query string.
 *
 * Every param the URL carries survives — the filters, the map viewport
 * (`north`/`south`/…, which are not filter-codec params at all) and anything
 * else a URL happens to carry — because a page-2 link that dropped the user's
 * district or price band would be a link to a different search. It is a
 * pass-through of the WHOLE query string rather than a re-encode of the
 * decoded filter set, which is what makes "anything else" true; that is why
 * the sort param's retirement (#6843, and the wire enum with #6927) changed
 * nothing here. The page number is written through the codec's ONE page
 * writer (`applyListingsPage`), which takes a number and nothing else — it is
 * the only public door that can spell the `page` param, and no filter can be
 * written through it (#8721) — and page 1 is written as NO param so the first
 * page has one URL. The base is a `Route` from a route function, so
 * `no-raw-route-constructor` holds: nothing here mints a path from a string.
 */
export function useListingsPageHref(base: Route): (page: number) => Route {
  const searchParams = useSearchParams();
  return useCallback(
    (page: number) => {
      const params = applyListingsPage(searchParams, page);
      return withQuery(base, Object.fromEntries(params));
    },
    [base, searchParams]
  );
}

export interface ListingsPagerProps {
  /** The served document's page — the loader's, never the infinite query's. */
  page: number;
  /** Whether the served document has a page after it — the loader's, likewise. */
  hasMore: boolean;
  /**
   * Where the served page stands against the search's last page — the
   * loader's, likewise (#6922). Matched exhaustively below: a page past the
   * end has no "Previous"/"Next" neighbourhood worth walking (its previous page
   * may be past the end too), so its arm is the one way back that always
   * exists — page 1 of the SAME search.
   */
  standing: PageStanding;
  /** Target route for page `n` — `useListingsPageHref(base)`. */
  hrefForPage: (page: number) => Route;
}

export function ListingsPager({ page, hasMore, standing, hrefForPage }: ListingsPagerProps) {
  const chrome = usePagerChrome();
  const t = useTranslations('listings.browse');
  const pageLink = cn(buttonVariants({ variant: 'outline', size: 'sm' }), 'min-w-9');

  switch (standing.kind) {
    case 'pastTheEnd':
      // The declared way back (#6922). `hrefForPage(1)` keeps every filter and
      // the viewport and drops `page`, so this is page 1 of the search the
      // reader is in — not the bare hub. No `rel`: page 1 is not this page's
      // neighbour, and the document is `noindex,follow` anyway.
      return (
        <nav aria-label={chrome.label} className="mt-6 flex items-center justify-center gap-2">
          <Link href={hrefForPage(1)} prefetch="intent" className={pageLink}>
            <ChevronLeft aria-hidden className="h-4 w-4" />
            {t('backToFirstPage')}
          </Link>
        </nav>
      );
    case 'within':
      return <WithinPager page={page} hasMore={hasMore} hrefForPage={hrefForPage} />;
    default:
      return assertNever(standing);
  }
}

function WithinPager({
  page,
  hasMore,
  hrefForPage,
}: Pick<ListingsPagerProps, 'page' | 'hasMore' | 'hrefForPage'>) {
  const chrome = usePagerChrome();

  // One page of results: no pager chrome at all. Rendering "Previous" and
  // "Next" with nowhere to go would be its own defect (`pagination.spec.ts`
  // asserts the same absence for the directories).
  if (page <= 1 && !hasMore) return null;

  const pageLink = cn(buttonVariants({ variant: 'outline', size: 'sm' }), 'min-w-9');

  return (
    <nav aria-label={chrome.label} className="mt-6 flex items-center justify-center gap-2">
      {page > 1 && (
        <Link
          href={hrefForPage(page - 1)}
          rel="prev"
          prefetch="intent"
          className={pageLink}
          aria-label={chrome.previous}
        >
          <ChevronLeft aria-hidden className="h-4 w-4" />
          {chrome.previous}
        </Link>
      )}
      {hasMore && (
        <Link
          href={hrefForPage(page + 1)}
          rel="next"
          prefetch="intent"
          className={pageLink}
          aria-label={chrome.next}
        >
          {chrome.next}
          <ChevronRight aria-hidden className="h-4 w-4" />
        </Link>
      )}
    </nav>
  );
}
